/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import PropTypes from 'prop-types';
import DayPicker from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import classNames from 'classnames';
import locales from './Locales';

import styles from './DateType.module.scss';

/**
 * This is the DateType component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function DateType({ labels, locale, state, onClick, onClickPredefined }) {
  const [from, to] = state ? state.split('_') : [null, null];
  const range = {
    from: from ? new Date(`${from}T12:00`) : null,
    to: to ? new Date(`${to}T12:00`) : null,
  };

  const Day = (day, modifiers) => {
    return <span>{day.getDate()}</span>;
  };

  return (
    <div className={styles.main}>
      <DayPicker
        className={classNames(styles.calendar, { [styles.single]: !range.to })}
        locale={locale}
        localeUtils={locales}
        selectedDays={[range.from, { from: range.from, to: range.to }]}
        modifiers={{
          start: range.from,
          end: range.to,
          disabled: { before: new Date() },
        }}
        // showOutsideDays
        renderDay={Day}
        onDayClick={(day) => {
          onClick(day);
        }}
      />
      <div className={styles.predefined}>
        <button className="chip" onClick={() => onClickPredefined('today')}>
          {labels.rangeToday}
        </button>
        <button className="chip" onClick={() => onClickPredefined('weekend')}>
          {labels.rangeWeekend}
        </button>
        <button className="chip" onClick={() => onClickPredefined('month')}>
          {labels.rangeMonth}
        </button>
        <button className="chip" onClick={() => onClickPredefined('upcoming')}>
          {labels.rangeUpcoming}
        </button>
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
DateType.propTypes = {
  locale: PropTypes.oneOf(['nb', 'en']),
  onClick: PropTypes.func,
  onClickPredefined: PropTypes.func,
  labels: PropTypes.shape({
    rangeToday: PropTypes.string,
    rangeWeekend: PropTypes.string,
    rangeMonth: PropTypes.string,
    rangeUpcoming: PropTypes.string,
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
DateType.defaultProps = {
  locale: 'nb',
  onClick: () => {},
  onClickPredefined: () => {},
  labels: {
    rangeToday: 'I dag',
    rangeWeekend: 'Neste helg',
    rangeMonth: 'Denne måneden',
    rangeUpcoming: 'Kommende',
  },
};

export default DateType;
